import React, { useEffect } from 'react';
import Button, { LinkButton } from '@atlaskit/button/new';
import EditionsIcon from '@atlaskit/icon-lab/core/editions';
import { useIntl, type MessageDescriptor } from '@atlassian/jira-intl';
import { useGetJsmPremiumBillingUrl } from '@atlassian/jira-servicedesk-standard-to-premium-modal/src/services/use-get-jsm-premium-billing-url/index.tsx';
import { useFlagService } from '@atlassian/jira-flags';
import { fireUIAnalytics, type UIAnalyticsEvent } from '@atlassian/jira-product-analytics-bridge';
import { messages } from './messages.tsx';

type UpsellButtonProps = {
	// Consumer component can pass in an origin to fire analytics event
	analyticsOrigin?: string;
	buttonLabel?: MessageDescriptor;
};

export const UpsellButton = ({ analyticsOrigin, buttonLabel }: UpsellButtonProps) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagService();
	const {
		data: { billingPageUrl },
		error,
	} = useGetJsmPremiumBillingUrl();

	useEffect(() => {
		if (!error) {
			return;
		}

		showFlag({
			type: 'error',
			isAutoDismiss: true,
			appearance: 'error',
			title: formatMessage(messages.errorFlagTitle),
			description: formatMessage(messages.errorFlagDescription),
			messageId:
				'servicedesk-premium-landing-page.ui.upsell-button.show-flag.error.try-it-free-button.show-flag.error',
			messageType: 'transactional',
		});
	}, [error, formatMessage, showFlag]);

	const handleOnClick = (
		_: React.MouseEvent<HTMLElement, MouseEvent>,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		if (analyticsOrigin) {
			fireUIAnalytics(analyticsEvent, 'premiumLandingPageUpsellButton', {
				landingPageOrigin: analyticsOrigin,
			});
		}
	};

	if (!billingPageUrl) {
		return (
			<Button
				isLoading
				testId="servicedesk-premium-landing-page.ui.upsell-button.try-it-free-button-loading"
			>
				{formatMessage(messages.tryItFreeButtonLabel)}
			</Button>
		);
	}

	return (
		<LinkButton
			appearance="discovery"
			onClick={handleOnClick}
			href={billingPageUrl}
			target="_blank"
			testId="servicedesk-premium-landing-page.ui.upsell-button.try-it-free-button"
			interactionName="servicedesk-premium-landing-page.ui.upsell-button.try-it-free-button"
			iconBefore={EditionsIcon}
		>
			{formatMessage(buttonLabel ?? messages.tryItFreeButtonLabel)}
		</LinkButton>
	);
};
